// ***************************************************************************************
// The orchestration: engine, renderer, storage and network in one place.
//
// The ordering rule this file exists to enforce: the LOGICAL state changes exactly once
// per move, here, and the animation is told about it afterwards. Input is locked while
// the board is moving, so a second submission cannot arrive mid-collapse and be applied
// against a board that no longer exists.
// ***************************************************************************************

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { loadDictionary, type Dictionary } from '../engine/dictionary.ts';
import {
  applyMove,
  applyShuffle,
  createGame,
  isCleared,
  priceSelection,
  replayActions,
  resign,
  restart as restartGame,
  shufflesLeft,
  tilesRemaining,
  undo as undoMove,
  type GameAction,
  type GameState,
  type PuzzleIdentity,
  type SelectionPricing,
} from '../engine/game.ts';
import { DAILY_SCORE_ATTACK, modeById, type ModeDefinition, type TileRef } from '../engine/modes.ts';
import { dayNumberNow } from '../engine/board.ts';
import { fetchPuzzle, fetchTodaysPuzzle, renamePlayer } from './api.ts';
import { extendSelection, tapDecision } from './tapDecision.ts';
import {
  clearAttempt,
  loadAttempt,
  nameFromUrl,
  playerName,
  savePlayerName,
  liveStreak,
  loadBest,
  loadStreak,
  playerId,
  recordBest,
  recordStreak,
  saveAttempt,
  type StoredAction,
  type StoredBest,
} from './persist.ts';
import type { BoardHandle } from '../ui/Board.tsx';
import type { SolverRequest, SolverResponse } from '../workers/solver.worker.ts';

const DICTIONARY_VERSION = '2016-04-12';

export type Phase = 'loading' | 'entry' | 'playing' | 'finished' | 'error';

export interface GameApi {
  phase: Phase;
  error: string | null;
  puzzle: PuzzleIdentity | null;
  /** The mode this deployment serves, as the server declared it. */
  mode: ModeDefinition;
  state: GameState | null;
  selection: readonly TileRef[];
  pricing: SelectionPricing | null;
  busy: boolean;
  best: StoredBest | null;
  hasSavedAttempt: boolean;
  /** Days in a row finished, as it stands today. 0 when there is no live streak. */
  streak: number;
  /** The name on the board, or null while the player is Anonymous. */
  name: string | null;
  /**
   * Set it. Renames every score already posted, so old rows stop saying Anonymous.
   *
   * Resolves when the server has been told, so a caller that reloads the leaderboard
   * afterwards sees the new name rather than racing the request it just fired.
   */
  setName(name: string): Promise<void>;

  begin(): void;
  resume(): void;
  tileDown(x: number, y: number): void;
  tileEnter(x: number, y: number): void;
  tileActivate(x: number, y: number): void;
  gestureEnd(): void;
  cancelSelection(): void;
  backspace(): void;
  submit(): void;
  undo(): void;
  restart(): void;
  finishEarly(): void;
  shuffle(): void;
  /** How many shuffles remain. 0 in modes that do not offer them. */
  shufflesLeft: number;
}

/** The stored log, back into engine actions. */
function toActions(stored: readonly StoredAction[]): GameAction[] {
  return stored.map((entry) =>
    Array.isArray(entry) ? { kind: 'word' as const, tiles: entry } : { kind: 'shuffle' as const },
  );
}

/** Engine actions, into the stored log. */
function toStored(actions: readonly GameAction[]): StoredAction[] {
  return actions.map((action) =>
    action.kind === 'shuffle'
      ? { shuffle: true as const }
      : action.tiles.map((tile) => ({ x: tile.x, y: tile.y })),
  );
}

function adjacent(a: TileRef, b: TileRef): boolean {
  return Math.abs(a.x - b.x) <= 1 && Math.abs(a.y - b.y) <= 1;
}

export function useGame(boardRef: React.RefObject<BoardHandle | null>): GameApi {
  const [phase, setPhase] = useState<Phase>('loading');
  const [error, setError] = useState<string | null>(null);
  const [puzzle, setPuzzle] = useState<PuzzleIdentity | null>(null);
  const [dictionary, setDictionary] = useState<Dictionary | null>(null);
  const [state, setState] = useState<GameState | null>(null);
  const [selection, setSelection] = useState<TileRef[]>([]);
  const [busy, setBusy] = useState(false);
  const [best, setBest] = useState<StoredBest | null>(null);
  const [hasSavedAttempt, setHasSavedAttempt] = useState(false);
  const [streak, setStreak] = useState(0);
  const [name, setNameState] = useState<string | null>(null);

  // ***************************************************************************************
  // THE MODE COMES FROM THE SERVER, not from the build.
  //
  // Each deployment serves one mode - Score Attack on the main site, Ladder on its own -
  // and says which in the puzzle it hands out. The client supports both and plays
  // whichever it is given, so there is one bundle and no way to reach a mode the site it
  // was loaded from does not serve.
  // ***************************************************************************************
  const mode: ModeDefinition =
    (puzzle ? modeById(puzzle.modeId) : null) ?? DAILY_SCORE_ATTACK;
  const solver = useRef<Worker | null>(null);
  const searchId = useRef(0);
  const pendingResume = useRef<GameState | null>(null);

  // ***************************************************************************************
  // Loading. The puzzle and the dictionary are fetched together - they are both needed
  // before anything can be played, and serialising them would double the wait.
  // ***************************************************************************************
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // A name handed over by the hub, which cannot reach this origin's storage any
        // other way. Adopted once and then removed from the address bar, so the link a
        // player shares afterwards does not rename the person who follows it.
        const handed = nameFromUrl(location.search);

        if (handed) {
          savePlayerName(handed);

          // And put it on the scores this browser has ALREADY posted here. Somebody who
          // played anonymously and then named themselves on the hub means the name to
          // apply to their scores, not just to the next one.
          void renamePlayer(playerId(), handed);

          const url = new URL(location.href);

          url.searchParams.delete('name');
          history.replaceState(null, '', url.toString());
        }

        setNameState(playerName());

        const requested = new URLSearchParams(location.search).get('puzzle');

        const [identity, dict] = await Promise.all([
          requested ? fetchPuzzle(requested) : fetchTodaysPuzzle(),
          loadDictionary({ base: '/dictionary', version: DICTIONARY_VERSION, withCommon: false }),
        ]);

        if (cancelled) return;

        setPuzzle(identity);
        setDictionary(dict);
        setBest(loadBest(identity.puzzleId));
        setStreak(liveStreak(loadStreak(), dayNumberNow()));

        // A saved attempt is restored by REPLAYING its moves, so a save written
        // mid-animation cannot restore into a board that play could not have reached.
        const saved = loadAttempt();

        if (
          saved &&
          saved.puzzleId === identity.puzzleId &&
          saved.rulesVersion === identity.rulesVersion &&
          saved.actions.length > 0
        ) {
          const savedMode = modeById(identity.modeId) ?? DAILY_SCORE_ATTACK;

          const replayed = replayActions(identity, toActions(saved.actions), dict, savedMode);

          if (replayed.ok && replayed.state.status === 'playing') {
            pendingResume.current = replayed.state;
            setHasSavedAttempt(true);
          } else {
            // A save that no longer replays is discarded rather than half-applied.
            clearAttempt();
          }
        }

        setPhase('entry');
      } catch (thrown: unknown) {
        if (cancelled) return;

        setError(
          thrown instanceof Error ? thrown.message : 'the puzzle could not be loaded',
        );
        setPhase('error');
      }
    })();

    return () => {
      cancelled = true;
    };
    // Runs once. `mode` is derived from the puzzle this effect fetches, so depending on
    // it here would re-run the load every time the mode resolved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ***************************************************************************************
  // The move-search worker.
  // ***************************************************************************************
  useEffect(() => {
    const worker = new Worker(new URL('../workers/solver.worker.ts', import.meta.url), {
      type: 'module',
    });

    const init: SolverRequest = {
      type: 'init',
      base: '/dictionary',
      version: DICTIONARY_VERSION,
    };

    worker.postMessage(init);

    solver.current = worker;

    return () => {
      worker.terminate();
      solver.current = null;
    };
  }, []);

  /**
   * Ask whether any move remains. Only a definite 'no' ends the game - 'unknown' means
   * the search was cut short, and ending somebody's game on that would be a bug with a
   * word still sitting on the board in front of them.
   */
  const checkForMoves = useCallback(
    (candidate: GameState): void => {
      const worker = solver.current;

      if (!worker) return;
      if (candidate.status !== 'playing') return;
      if (isCleared(candidate.board)) return;

      const id = ++searchId.current;

      const onMessage = (event: MessageEvent<SolverResponse>): void => {
        const message = event.data;

        if (message.type !== 'result' || message.id !== id) return;

        worker.removeEventListener('message', onMessage);

        if (message.outcome !== 'no') return;

        setState((current) => {
          if (!current || current.status !== 'playing') return current;

          // Guard against a stale answer: only finish if the board has not moved on.
          if (current.moves.length !== candidate.moves.length) return current;

          return { ...current, status: 'finished', finishReason: 'no-moves' };
        });
      };

      worker.addEventListener('message', onMessage);

      const lengths = mode.playableLengths(candidate.moves);

      const request: SolverRequest = {
        type: 'search',
        id,
        board: candidate.board.slice(),
        w: candidate.puzzle.w,
        h: candidate.puzzle.h,
        adjacency: mode.requiresAdjacency,
        ...(lengths === null ? {} : { lengths }),
      };

      worker.postMessage(request);
    },
    [mode],
  );

  // ***************************************************************************************
  // Persistence. Written after every committed move, undo and restart.
  // ***************************************************************************************
  const persist = useCallback((candidate: GameState): void => {
    saveAttempt({
      puzzleId: candidate.puzzle.puzzleId,
      modeId: candidate.modeId,
      rulesVersion: candidate.puzzle.rulesVersion,
      dictionaryVersion: candidate.puzzle.dictionaryVersion,
      actions: toStored(candidate.actions),
      status: candidate.status,
      finishReason: candidate.finishReason,
      score: candidate.score,
      savedAt: Date.now(),
    });
  }, []);

  // When a game finishes, record the best score locally. This happens whether or not the
  // server can be reached, and a restart can never undo it.
  useEffect(() => {
    if (!state || state.status !== 'finished' || !puzzle) return;

    let longest = 0;

    for (const move of state.moves) {
      if (move.word.length > longest) longest = move.word.length;
    }

    const improved = recordBest({
      puzzleId: puzzle.puzzleId,
      score: state.score,
      words: state.moves.length,
      longestWordLength: longest,
      cleared: isCleared(state.board),
      tilesRemaining: tilesRemaining(state.board),
      confirmed: false,
      savedAt: Date.now(),
    });

    if (improved) setBest(loadBest(puzzle.puzzleId));

    // THE STREAK IS RECORDED ON FINISHING, not on starting, and against the PUZZLE'S day
    // rather than today's - so following a shared link to an old puzzle cannot rewrite a
    // live streak. recordStreak ignores a day it has already counted, so restarting and
    // finishing again does not double it.
    setStreak(liveStreak(recordStreak(puzzle.day), dayNumberNow()));

    setPhase('finished');
  }, [state, puzzle]);

  // ***************************************************************************************
  // Starting
  // ***************************************************************************************
  const begin = useCallback(() => {
    if (!puzzle) return;

    const fresh = createGame(puzzle, mode);

    pendingResume.current = null;
    setHasSavedAttempt(false);
    setState(fresh);
    setSelection([]);
    setPhase('playing');
    persist(fresh);

    queueMicrotask(() => boardRef.current?.setBoard(fresh.board));
  }, [puzzle, mode, persist, boardRef]);

  const resume = useCallback(() => {
    const saved = pendingResume.current;

    if (!saved) {
      begin();
      return;
    }

    setState(saved);
    setSelection([]);
    setPhase('playing');

    queueMicrotask(() => boardRef.current?.setBoard(saved.board));
  }, [begin, boardRef]);

  // ***************************************************************************************
  // Selection
  // ***************************************************************************************
  // The rule itself is in tapDecision.ts, pure and tested. This is the wiring - and the
  // cast is only because setSelection holds a mutable array while the rule returns a
  // readonly one; the contents are the same tiles.
  const extend = useCallback(
    (x: number, y: number) => {
      setSelection(
        (current) =>
          extendSelection(current, { x, y }, mode.requiresAdjacency) as TileRef[],
      );
    },
    [mode],
  );

  // ***************************************************************************************
  // Committing a move.
  //
  // Takes the tiles EXPLICITLY rather than reading the selection, so a caller that has
  // just decided what the word is can commit exactly that. Reading component state here
  // would make the result depend on whether React had processed a setSelection yet.
  //
  // The logical state moves once, here. Everything after is presentation.
  // ***************************************************************************************
  const submitTiles = useCallback(
    (tiles: readonly TileRef[]) => {
      if (busy || !state || !dictionary || tiles.length === 0) return;

      const result = applyMove(state, tiles, dictionary, mode);

      if (!result.ok) return;

      const removed = tiles.map((tile) => ({ x: tile.x, y: tile.y }));

      setBusy(true);
      setState(result.state);
      setSelection([]);
      persist(result.state);

      void (async () => {
        try {
          await boardRef.current?.playMove(removed);
        } finally {
          setBusy(false);
        }

        checkForMoves(result.state);
      })();
    },
    [busy, state, dictionary, mode, persist, boardRef, checkForMoves],
  );

  // ***************************************************************************************
  // Tap and drag are the SAME gesture until the pointer moves.
  //
  // A pointer-down cannot know which it is yet, and guessing wrong breaks one of them:
  // resetting the selection on every down makes tapping out a word impossible (each tap
  // replaces the last), while extending on every down makes a fresh drag continue the
  // previous word. So the decision is deferred to the first move, or to the release.
  // ***************************************************************************************
  const pendingTile = useRef<TileRef | null>(null);
  const dragged = useRef(false);
  const startedEmpty = useRef(true);

  const tileDown = useCallback(
    (x: number, y: number) => {
      if (busy) return;

      pendingTile.current = { x, y };
      dragged.current = false;
      startedEmpty.current = selection.length === 0;

      // With nothing selected, tap and drag both want this tile highlighted at once, so
      // there is no reason to wait and every reason not to (the lag is visible).
      if (selection.length === 0) setSelection([{ x, y }]);
    },
    [busy, selection.length],
  );

  const tileEnter = useCallback(
    (x: number, y: number) => {
      if (busy) return;

      const pending = pendingTile.current;

      if (!dragged.current) {
        dragged.current = true;

        // The gesture is a DRAG, so it starts a new word from wherever it began.
        if (pending && !startedEmpty.current) {
          const reachable = !mode.requiresAdjacency || adjacent(pending, { x, y });

          setSelection(reachable ? [pending, { x, y }] : [pending]);

          return;
        }
      }

      extend(x, y);
    },
    [busy, extend, mode],
  );

  /**
   * Tap-to-select, and the accessible grid's activation.
   *
   * The rule itself lives in tapDecision.ts as a pure function, so it can be tested
   * without a browser. This is only the wiring.
   */
  const tileActivate = useCallback(
    (x: number, y: number) => {
      if (busy || !state || !dictionary) return;

      const playable =
        selection.length > 0 && priceSelection(state, selection, dictionary, mode).valid;

      const action = tapDecision(selection, { x, y }, playable, mode.requiresAdjacency);

      switch (action.kind) {
        case 'start':
          setSelection([action.tile]);
          break;

        case 'extend':
          setSelection([...selection, action.tile]);
          break;

        case 'remove-last':
          setSelection(selection.slice(0, -1));
          break;

        case 'truncate':
          setSelection(selection.slice(0, action.length));
          break;

        case 'submit':
          submitTiles(selection);
          break;
      }
    },
    [busy, state, dictionary, mode, selection, submitTiles],
  );

  // A released gesture does NOT submit. Submission is always explicit - lifting a finger
  // is far too easy to do by accident for it to commit a word.
  //
  // What a release DOES decide is whether the gesture was a tap. A pointer that never
  // moved to another tile is a tap, and a tap extends the word rather than restarting it.
  const gestureEnd = useCallback(() => {
    const pending = pendingTile.current;

    pendingTile.current = null;

    if (busy || dragged.current || !pending) return;

    // A tap that began with nothing selected already highlighted its tile on the way
    // down; acting again here would immediately un-select it.
    if (startedEmpty.current) return;

    tileActivate(pending.x, pending.y);
  }, [busy, tileActivate]);

  // ***************************************************************************************
  // The name.
  //
  // Stored locally AND pushed to the server, because a name typed after a score has been
  // posted must fix the row that is already there. Otherwise a player names themselves,
  // looks at the board, and still sees Anonymous - which is precisely the bug this came
  // from.
  // ***************************************************************************************
  const setName = useCallback(async (next: string): Promise<void> => {
    const trimmed = next.trim().slice(0, 20);

    savePlayerName(trimmed);
    setNameState(trimmed.length > 0 ? trimmed : null);

    await renamePlayer(playerId(), trimmed.length > 0 ? trimmed : null);
  }, []);

  const cancelSelection = useCallback(() => setSelection([]), []);

  const backspace = useCallback(() => {
    setSelection((current) => current.slice(0, -1));
  }, []);

  const pricing = useMemo(() => {
    if (!state || !dictionary || selection.length === 0) return null;

    return priceSelection(state, selection, dictionary, mode);
  }, [state, dictionary, selection, mode]);

  const submit = useCallback(() => {
    submitTiles(selection);
  }, [submitTiles, selection]);

  const undo = useCallback(() => {
    // Undo during an animation is refused rather than queued. Allowing it would mean
    // reasoning about a board that is part way between two states.
    if (busy || !state || !dictionary || state.moves.length === 0) return;

    const back = undoMove(state, dictionary, mode);

    setState(back);
    setSelection([]);
    persist(back);

    boardRef.current?.setBoard(back.board);
  }, [busy, state, dictionary, mode, persist, boardRef]);

  const restart = useCallback(() => {
    if (busy || !state) return;

    const fresh = restartGame(state, mode);

    setState(fresh);
    setSelection([]);
    setPhase('playing');
    persist(fresh);

    boardRef.current?.setBoard(fresh.board);
  }, [busy, state, mode, persist, boardRef]);

  const shuffle = useCallback(() => {
    if (busy || !state) return;

    const result = applyShuffle(state);

    if (!result.ok) return;

    setBusy(true);
    setState(result.state);
    setSelection([]);
    persist(result.state);

    // Input is locked while the tiles turn over, exactly as it is during a collapse. The
    // logical board has ALREADY changed - the animation is only showing it happening -
    // so a word begun mid-flip would be picked from letters that are no longer there.
    void (async () => {
      try {
        await boardRef.current?.playShuffle(result.state.board);
      } finally {
        setBusy(false);
      }

      // A shuffle changes what is playable, so the no-moves answer from before is stale.
      checkForMoves(result.state);
    })();
  }, [busy, state, persist, boardRef, checkForMoves]);

  const finishEarly = useCallback(() => {
    if (busy || !state) return;

    const done = resign(state);

    setState(done);
    setSelection([]);
    persist(done);
  }, [busy, state, persist]);

  return {
    phase,
    error,
    puzzle,
    mode,
    state,
    selection,
    pricing,
    busy,
    best,
    hasSavedAttempt,
    streak,
    name,
    setName,
    begin,
    resume,
    tileDown,
    tileEnter,
    tileActivate,
    gestureEnd,
    cancelSelection,
    backspace,
    submit,
    undo,
    restart,
    finishEarly,
    shuffle,
    shufflesLeft: state ? shufflesLeft(state) : 0,
  };
}

export { playerId };
